/**
 * Highland Theme Context
 *
 * Manages light/dark mode for the application.
 * Theme preference is persisted to localStorage.
 * Wires Highland design tokens into MUI createTheme().
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import {
  RADIUS_SM,
  RADIUS_MD,
  RADIUS_LG,
  SHADOW_CARD_LIGHT,
  SHADOW_CARD_DARK,
  TRANSITION_FAST,
  TRANSITION_NORMAL,
  EASING,
  FOCUS_RING_COLOUR,
  SPACING_SM,
  SPACING_MD,
  SPACING_LG,
} from '../designTokens';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// =============================================================================
// HIGHLAND BRAND COLORS
// =============================================================================
const brandColors = {
  // Primary - Highland Orange palette
  primary: '#ed8b00',       // Highland Orange (main)
  primaryLight: '#ffb81c',  // Amber
  primaryDark: '#fe5000',   // Flame

  // Secondary - Purple/Navy palette
  secondary: '#4e008e',     // Purple
  secondaryLight: '#366287', // Light Denim
  secondaryDark: '#253746', // Navy (AppBar background)

  // Semantic colors
  success: '#00a86b',
  error: '#f83600',         // Crimson
  warning: '#ffb81c',       // Amber
  info: '#366287',          // Light Denim

  // Text colors
  textPrimary: '#2d2926',   // Rich Black
  textSecondary: '#373a36', // Soot
};

// =============================================================================
// SHARED TYPOGRAPHY
// =============================================================================
const headingBase = {
  fontFamily: '"brandon-grotesque", "Open Sans", sans-serif',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase' as const,
};

const sharedTypography = {
  fontFamily: '"Open Sans", "Segoe UI", "Arial", sans-serif',
  h1: { ...headingBase },
  h2: { ...headingBase },
  h3: { ...headingBase },
  h4: {
    ...headingBase,
    fontSize: '1.5rem',
    lineHeight: 1.235,
  },
  h5: { ...headingBase },
  h6: { ...headingBase },
  button: {
    fontFamily: '"brandon-grotesque", "Open Sans", sans-serif',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  body1: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.43,
  },
};

// =============================================================================
// SHARED COMPONENT OVERRIDES
// =============================================================================
const sharedComponents = (mode: ThemeMode) => ({
  MuiCssBaseline: {
    styleOverrides: {
      '*, *::before, *::after': {
        transition: `background-color ${TRANSITION_NORMAL} ${EASING}, border-color ${TRANSITION_FAST} ${EASING}`,
      },
      'a:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible': {
        outline: `2px solid ${FOCUS_RING_COLOUR}`,
        outlineOffset: '2px',
      },
    },
  },
  MuiButtonBase: {
    styleOverrides: {
      root: {
        '&:focus-visible': {
          outline: `2px solid ${FOCUS_RING_COLOUR}`,
          outlineOffset: '2px',
        },
        transition: `all ${TRANSITION_FAST} ${EASING}`,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS_MD,
        textTransform: 'uppercase' as const,
        padding: `${SPACING_SM}px ${SPACING_MD}px`,
        boxShadow: 'none',
        fontFamily: '"brandon-grotesque", "Open Sans", sans-serif',
        fontWeight: 700,
        letterSpacing: '0.05em',
        transition: `all ${TRANSITION_FAST} ${EASING}`,
      },
      contained: {
        '&:hover': {
          boxShadow: '0 2px 8px rgba(237, 139, 0, 0.3)',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        boxShadow: mode === 'light' ? SHADOW_CARD_LIGHT : SHADOW_CARD_DARK,
        borderRadius: RADIUS_MD,
        transition: `box-shadow ${TRANSITION_NORMAL} ${EASING}`,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS_MD,
        ...(mode === 'dark' ? { backgroundImage: 'none' } : {}),
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: SPACING_LG,
        '&:last-child': {
          paddingBottom: SPACING_LG,
        },
      },
    },
  },
  MuiTableContainer: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS_MD,
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        background: mode === 'light'
          ? 'linear-gradient(135deg, #253746 0%, #366287 100%)'
          : '#2a2a2a',
        '& .MuiTableCell-head': {
          backgroundColor: 'transparent',
          color: '#ffffff',
          fontFamily: '"brandon-grotesque", "Open Sans", sans-serif',
          fontWeight: 700,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.05em',
          fontSize: '0.875rem',
        },
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        transition: `background-color ${TRANSITION_FAST} ${EASING}`,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontFamily: '"brandon-grotesque", "Open Sans", sans-serif',
        fontWeight: 700,
        textTransform: 'uppercase' as const,
        fontSize: '0.75rem',
        borderRadius: RADIUS_SM,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: RADIUS_LG,
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: brandColors.secondaryDark,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        borderRadius: 0,
        ...(mode === 'dark' ? { backgroundImage: 'none' } : {}),
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        fontFamily: '"brandon-grotesque", "Open Sans", sans-serif',
        fontWeight: 700,
        letterSpacing: '0.05em',
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontFamily: '"brandon-grotesque", "Open Sans", sans-serif',
        fontWeight: 700,
        letterSpacing: '0.05em',
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontFamily: '"Open Sans", "Segoe UI", "Arial", sans-serif',
        fontWeight: 600,
      },
    },
  },
});

// =============================================================================
// LIGHT THEME
// =============================================================================
const createLightTheme = () => createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brandColors.primary,
      light: brandColors.primaryLight,
      dark: brandColors.primaryDark,
      contrastText: '#fff',
    },
    secondary: {
      main: brandColors.secondary,
      light: brandColors.secondaryLight,
      dark: brandColors.secondaryDark,
      contrastText: '#fff',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    text: {
      primary: brandColors.textPrimary,
      secondary: brandColors.textSecondary,
    },
    success: { main: brandColors.success },
    error: { main: brandColors.error },
    warning: { main: brandColors.warning },
    info: { main: brandColors.info },
  },
  typography: sharedTypography,
  components: sharedComponents('light'),
});

// =============================================================================
// DARK THEME
// =============================================================================
const createDarkTheme = () => createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: brandColors.primary,
      light: brandColors.primaryLight,
      dark: brandColors.primaryDark,
      contrastText: '#fff',
    },
    secondary: {
      main: brandColors.secondary,
      light: brandColors.secondaryLight,
      dark: brandColors.secondaryDark,
      contrastText: '#fff',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    },
    success: { main: brandColors.success },
    error: { main: brandColors.error },
    warning: { main: brandColors.warning },
    info: { main: brandColors.info },
  },
  typography: sharedTypography,
  components: sharedComponents('dark'),
});

// =============================================================================
// PROVIDER COMPONENT
// =============================================================================
export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>('light');

  // Load theme preference from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('highland-theme') as ThemeMode;
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      setMode(savedTheme);
    }
  }, []);

  const setTheme = useCallback((newMode: ThemeMode) => {
    setMode(newMode);
    localStorage.setItem('highland-theme', newMode);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(mode === 'light' ? 'dark' : 'light');
  }, [mode, setTheme]);

  const theme = useMemo(() => {
    return mode === 'light' ? createLightTheme() : createDarkTheme();
  }, [mode]);

  const value = useMemo(() => ({
    mode,
    toggleTheme,
    setTheme,
  }), [mode, toggleTheme, setTheme]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// =============================================================================
// HOOK
// =============================================================================
export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeMode must be used within a ThemeContextProvider');
  }
  return context;
};

export default ThemeContext;
